import { useMemo, useState } from 'react';
import type { RatesSettings } from '@/settings/types';
import type { CalculatorInputs } from './CalculatorForm';
import { compareFans } from '@/calc/fans';
import { calculateBill, unitsForBill } from '@/calc/tariff';
import { projectionTable } from '@/calc/projection';
import { ComparisonTable } from './ComparisonTable';
import { EffectiveUnitPrice } from './EffectiveUnitPrice';
import { SavingsProjection } from './SavingsProjection';
import { SavingsHero } from './SavingsHero';
import { formatKwh, formatKwh3, formatLkr } from '@/lib/format';

export function ResultsPanel({ inputs, rates }: { inputs: CalculatorInputs; rates: RatesSettings }) {
  const [showDetails, setShowDetails] = useState(false);

  const view = useMemo(() => {
    const fans = compareFans(inputs.totalFans, inputs.hoursPerDay, rates);

    let normalTotalUnits = 0;
    let snappedNote: string | null = null;

    if (inputs.mode === 'units') {
      normalTotalUnits = inputs.units ?? 0;
    } else {
      const r = unitsForBill(inputs.billLkr ?? 0, rates);
      normalTotalUnits = r.units;
      if (r.snapped) {
        snappedNote = `Bill snapped to the nearest attainable point (${formatLkr(r.bill)} ≈ ${formatKwh(r.units)}).`;
      }
    }

    const normalBill = calculateBill(normalTotalUnits, rates);
    const atombergTotalUnits = Math.max(0, normalTotalUnits - fans.unitsSavedPerMonth);
    const atombergBill = calculateBill(atombergTotalUnits, rates);

    const monthlySavingLkr = normalBill.totalLkr - atombergBill.totalLkr;
    const projections = projectionTable(monthlySavingLkr, inputs.totalFans, rates);

    return {
      fans,
      normalBill,
      atombergBill,
      monthlySavingLkr,
      projections,
      snappedNote,
    };
  }, [inputs, rates]);

  const {
    fans,
    normalBill,
    atombergBill,
    monthlySavingLkr,
    projections,
    snappedNote,
  } = view;

  const yearOne = projections.find((p) => p.years === 1);
  const yearThree = projections.find((p) => p.years === 3);

  return (
    <div className="space-y-5 sm:space-y-6">
      {snappedNote && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {snappedNote}
        </div>
      )}

      <SavingsHero
        newMonthlyBillLkr={atombergBill.totalLkr}
        yearlyTotalLkr={yearOne?.totalSavingLkr ?? monthlySavingLkr * 12}
        threeYearTotalLkr={yearThree?.totalSavingLkr ?? monthlySavingLkr * 36}
      />

      <div className="text-center">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="text-sm text-slate-600 underline underline-offset-2 hover:text-slate-900"
        >
          {showDetails ? 'Hide breakdown' : 'How is this calculated?'}
        </button>
      </div>

      {showDetails && (
        <div className="space-y-5 sm:space-y-6 pt-2">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <MiniCard label="Fan units saved / month" value={formatKwh3(fans.unitsSavedPerMonth)} />
            <MiniCard label="New monthly bill" value={formatLkr(atombergBill.totalLkr)} />
          </div>

          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Bill comparison</h3>
            <ComparisonTable fans={fans} normalBill={normalBill} atombergBill={atombergBill} />
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Effective unit price</h3>
            <EffectiveUnitPrice normalBill={normalBill} atombergBill={atombergBill} />
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Maintenance</h3>
            <div className="text-sm text-slate-700 rounded-lg border border-slate-200 p-4 bg-white space-y-1">
              <p>
                Normal fans need condenser service every{' '}
                <span className="font-medium">{rates.maintenance.cycleMonths} months</span>:
              </p>
              <ul className="list-disc ml-5">
                <li>
                  Electrician (one-time, per fan):{' '}
                  <span className="font-medium">{formatLkr(rates.maintenance.electricianLkr)}</span>
                </li>
                <li>
                  Condenser (per fan, per cycle):{' '}
                  <span className="font-medium">{formatLkr(rates.maintenance.condenserLkr)}</span>
                </li>
                <li>
                  Atomberg fans: <span className="font-medium">Rs. 0</span> maintenance.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Savings over time</h3>
            <SavingsProjection rows={projections} />
          </section>
        </div>
      )}
    </div>
  );
}

function MiniCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-base sm:text-lg font-semibold mt-1 tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
